/**
 * frontend/src/views/ReviewView.ts
 * ==================================
 * Vista 3 — Panel de revisión HITL (Human-in-the-Loop).
 *
 * Responsabilidades:
 *   - Mostrar el resumen de diferencias en una tabla HTML formateada
 *   - Mostrar los movimientos y ofrecer la descarga (VisorConciliacion)
 *   - Enviar el reporte por correo, o no enviarlo: el resultado ya está a la
 *     vista y para descargar, así que lo único que queda es si va por correo
 *   - Llamar a enviarDecision() y transicionar al estado "procesando" (fase 2)
 */

import { enviarDecision, ApiError } from "../api";
import { montarVisor } from "../components/VisorConciliacion";
import type { AppState } from "../types";

import { escapeHtml as _escape, fmtPesos as _fmt } from "../utils/texto";
import { problemaDeDestinatarios } from "../utils/destinatarios";
import { icon } from "../utils/icons";

/** Espejo de EstadoPeriodo del backend (src/conciliacion/clasificador.py). */
type EstadoPeriodo = "conciliado" | "conciliado_con_pendientes" | "diferencia";

interface PeriodoResumen {
  periodo: string;
  estado: EstadoPeriodo;
  saldo_banco: number;
  saldo_mayor: number;
  tipo_a: { count: number; monto: number };
  tipo_b: { count: number; monto: number };
}

/**
 * Badges por estado. Las palabras replican las etiquetas del clasificador:
 * lo que se aprueba acá es lo que después dice el reporte, textual.
 *
 * Los dos estados conciliados comparten texto a propósito — el saldo cierra
 * en ambos. La diferencia la cuenta el color: verde sin pendientes, amarillo
 * cuando quedan movimientos Tipo A/B por revisar. Igual que en Excel y PDF.
 */
const BADGE_ESTADO: Record<EstadoPeriodo, string> = {
  conciliado:
    `<span class="badge badge--ok">${icon("check")} Conciliado</span>`,
  conciliado_con_pendientes:
    `<span class="badge badge--pend">${icon("check")} Conciliado</span>`,
  diferencia:
    `<span class="badge badge--warn">${icon("alert")} Diferencia</span>`,
};

export interface OpcionesRevision {
  /** Los destinatarios vigentes. Getter: se pueden indicar mientras se revisa. */
  destinatarios: () => string;
  /** Abre Preferencias en los destinatarios; `alGuardar` corre si se guardan. */
  abrirDestinatarios: (alGuardar: () => void) => void;
  /**
   * Registra qué hacer cuando se guardan las Preferencias desde cualquier
   * lugar —también desde el botón de la cabecera—: el bloque de la decisión
   * tiene que nombrar a los destinatarios vigentes, no a los de antes.
   */
  alCambiarPreferencias?: (redibujar: () => void) => void;
}

export function renderReviewView(
  container: HTMLElement,
  state: AppState,
  onDecision: (decision: "aprobar" | "cancelar") => void,
  opciones: OpcionesRevision = { destinatarios: () => "", abrirDestinatarios: () => {} },
): void {
  container.innerHTML = "";

  const wrapper = document.createElement("div");
  wrapper.className = "review-view";

  wrapper.innerHTML = `
    <header class="review-view__header">
      <h2 class="review-view__title">Revisión de conciliación</h2>
      <p class="review-view__subtitle">
        Revise el resumen y los movimientos antes de decidir si el reporte se envía por correo.
      </p>
    </header>

    <div class="review-view__resumen" id="resumen-content" aria-live="polite">
      ${_renderResumen(state.resumen)}
    </div>

    <section id="visor-conciliacion" aria-label="Movimientos de la conciliación"></section>

    <div class="review-view__error" id="error-msg" role="alert" hidden></div>

    <div class="review-view__decision">
      <div class="review-view__envio" id="envio-info" aria-live="polite"></div>
      <div class="review-view__actions">
        <button class="btn btn--danger-outline" id="btn-rechazar" type="button">
          ${icon("close")} No enviar el reporte
        </button>
        <button class="btn btn--success" id="btn-aceptar" type="button">
          ${icon("mail")} Enviar reporte por correo
        </button>
      </div>
    </div>
  `;
  container.appendChild(wrapper);
  if (state.threadId) montarVisor(wrapper.querySelector<HTMLElement>("#visor-conciliacion")!, state.threadId);

  const errorEl = wrapper.querySelector<HTMLElement>("#error-msg")!;
  const btnAceptar = wrapper.querySelector<HTMLButtonElement>("#btn-aceptar")!;
  const btnRechazar = wrapper.querySelector<HTMLButtonElement>("#btn-rechazar")!;
  const envioEl = wrapper.querySelector<HTMLElement>("#envio-info")!;

  // A quién va el reporte, al lado de los botones que deciden si va. Sin
  // destinatarios, el envío espera a que se indiquen; no enviar sigue a mano.
  const pintarEnvio = (): void => {
    const lista = opciones.destinatarios();
    const problema = problemaDeDestinatarios(lista);
    btnAceptar.disabled = problema !== null;
    envioEl.innerHTML = problema
      ? `${icon("mail")} <span>${_escape(problema)}</span>
         <button class="btn btn--secondary btn--sm" type="button" id="btn-destinatarios">Indicar destinatarios</button>`
      : `${icon("mail")} <span>Se envía en Excel y PDF a <strong>${_escape(lista.split(/[,;\s]+/).filter(Boolean).join(", "))}</strong>.</span>`;
    envioEl.querySelector("#btn-destinatarios")?.addEventListener("click", () => opciones.abrirDestinatarios(pintarEnvio));
  };
  pintarEnvio();
  opciones.alCambiarPreferencias?.(pintarEnvio);

  async function handleDecision(decision: "aprobar" | "cancelar"): Promise<void> {
    btnAceptar.disabled = true;
    btnRechazar.disabled = true;
    errorEl.hidden = true;

    try {
      await enviarDecision(state.threadId!, decision, opciones.destinatarios());
      onDecision(decision);
    } catch (err) {
      // 409 = la sesión ya no espera decisión porque el backend la tomó antes.
      // La operación sí ocurrió, así que avanzamos en vez de mostrar un error
      // que contradice lo que el usuario está por recibir.
      if (err instanceof ApiError && err.statusCode === 409) {
        onDecision(decision);
        return;
      }

      btnRechazar.disabled = false;
      pintarEnvio();
      errorEl.textContent = err instanceof ApiError
        ? err.message
        : "Error de comunicación con el servidor.";
      errorEl.hidden = false;
    }
  }

  btnAceptar.addEventListener("click", () => void handleDecision("aprobar"));
  btnRechazar.addEventListener("click", () => void handleDecision("cancelar"));
}

// ── Renderizado del resumen ────────────────────────────────────────────────────

function _renderResumen(raw: string | null): string {
  if (!raw) return "<p class='resumen-empty'>Sin resumen disponible.</p>";

  // Intentar parsear el JSON estructurado del backend
  try {
    const data = JSON.parse(raw) as { periodos: PeriodoResumen[] };
    if (data.periodos && Array.isArray(data.periodos)) {
      return _renderTabla(data.periodos);
    }
  } catch {
    // fallback: texto plano
  }

  // Fallback para formatos legacy o errores de parse
  return `<pre class="resumen-pre">${_escape(raw)}</pre>`;
}

function _renderTabla(periodos: PeriodoResumen[]): string {
  if (periodos.length === 0) {
    return "<p class='resumen-empty'>Sin períodos conciliados.</p>";
  }

  const filas = periodos.map(p => `
    <article class="period-row" aria-label="${_escape(p.periodo)}">
      <dl class="financial-grid">
        <div class="financial-stat"><dt>Período</dt><dd>${_escape(p.periodo)}</dd></div>
        <div class="financial-stat financial-stat--state"><dt>Estado</dt><dd>${BADGE_ESTADO[p.estado] ?? BADGE_ESTADO.diferencia}${p.estado === "conciliado_con_pendientes" ? '<p class="period-row__note">Movimientos por revisar</p>' : ""}</dd></div>
        <div class="financial-stat"><dt>Saldo banco</dt><dd>${_fmt(p.saldo_banco)}</dd></div>
        <div class="financial-stat"><dt>Saldo libro mayor</dt><dd>${_fmt(p.saldo_mayor)}</dd></div>
        <div class="financial-stat"><dt>Solo en banco</dt><dd>${p.tipo_a.count} <small>movs.</small><p>${_fmt(p.tipo_a.monto)}</p></dd></div>
        <div class="financial-stat"><dt>Solo en contabilidad</dt><dd>${p.tipo_b.count} <small>movs.</small><p>${_fmt(p.tipo_b.monto)}</p></dd></div>
      </dl>
    </article>`).join("");
  return `<section class="review-summary" aria-label="Resumen por período">${filas}</section>`;
}
